import { useEffect, useState, useRef } from 'react'
import { cn } from '@/tiptap/lib/tiptap-utils'
import { EmojiDropdownMenu } from '@/tiptap/tiptap-ui/emoji-dropdown-menu'
import { EmojiMenu } from '@/tiptap/tiptap-ui/emoji-menu'
import Emoji, { gitHubEmojis, type EmojiItem } from '@tiptap/extension-emoji'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useAtom } from 'jotai'
import {
  Bold,
  Italic,
  Strikethrough,
  AtSign,
  ArrowUp,
  X,
  Smile,
} from 'lucide-react'
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import { workspaceIdAtom } from '@/stores/workspaceStore'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'

export interface CommentEditorSubmitPayload {
  text: string
  json: any
  mentions: string[]
}

export interface CommentEditorProps {
  value?: string
  jsonValue?: any // For rich content JSON
  placeholder?: string
  autoFocus?: boolean
  minHeightClass?: string
  maxHeightClass?: string
  onSubmit?: (payload: CommentEditorSubmitPayload) => void
  onCancel?: () => void
  members?: Array<{ id: string; name?: string | null; email?: string | null; image?: string | null }>
  showBorder?: boolean
  showSubmitButton?: boolean
  onChangeContent?: (text: string, json: any, mentions: string[]) => void
  editorClassName?: string
}

function collectMentions(node: any, acc: string[] = []): string[] {
  if (!node) return acc
  if (node.type === 'mention' && node.attrs && node.attrs.id) {
    acc.push(String(node.attrs.id))
  }
  const children = (node as any).content
  if (Array.isArray(children)) {
    for (const child of children) collectMentions(child, acc)
  }
  return acc
}

export function CommentEditor({
  value,
  jsonValue,
  placeholder = 'Reply...',
  autoFocus = true,
  minHeightClass = '',
  maxHeightClass = 'max-h-40',
  onSubmit,
  onCancel,
  members,
  showBorder = true,
  showSubmitButton = true,
  onChangeContent,
  editorClassName,
}: CommentEditorProps) {
  const [_workspaceId] = useAtom(workspaceIdAtom)
  // Use ref to always access current members in suggestion callback
  const membersRef = useRef(members || [])
  membersRef.current = members || []
  // Track when mention popup is open to prevent Enter/Escape from being hijacked
  const mentionOpenRef = useRef(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: {
          keepMarks: true,
        },
      }),
      // Placeholder for empty editor
      Placeholder.configure({
        placeholder,
        emptyNodeClass: 'is-editor-empty',
      }),
      // Emoji support via @tiptap/extension-emoji
      Emoji.configure({
        emojis: gitHubEmojis.filter((e) => !e.name.includes('regional')),
        forceFallbackImages: true,
      }),
      Mention.configure({
        HTMLAttributes: { class: 'text-primary font-medium' },
        renderLabel: ({ node }) =>
          `@${node.attrs.label || node.attrs.name || node.attrs.email || 'user'}`,
        suggestion: {
          char: '@',
          items: ({ query }: any) => {
            const list = membersRef.current || []
            const q = (query || '').toLowerCase()
            return list
              .filter(
                (m: any) =>
                  (m.name || '').toLowerCase().includes(q) ||
                  (m.email || '').toLowerCase().includes(q)
              )
              .slice(0, 5)
              .map((m: any) => ({
                id: m.id,
                name: m.name,
                email: m.email,
                image: m.image,
                label: m.name || m.email,
              }))
          },
          render: () => {
            let popup: any
            let container: HTMLDivElement
            let selectedIndex = 0
            let currentItems: any[] = []
            let currentCommand: any = null

            const renderList = () => {
              if (!container) return
              container.innerHTML = ''
              const wrap = document.createElement('div')
              wrap.className =
                'rounded-md border bg-background p-1 shadow-md min-w-[220px]'
              currentItems.forEach((item: any, index: number) => {
                const btn = document.createElement('button')
                const isSelected = index === selectedIndex
                btn.className = `flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-foreground !cursor-pointer ${isSelected ? 'bg-accent' : 'hover:bg-accent'}`
                btn.dataset.index = String(index)

                // Avatar
                const avatar = document.createElement('div')
                avatar.className = 'flex-shrink-0 h-6 w-6 rounded-full bg-muted flex items-center justify-center overflow-hidden'
                if (item.image) {
                  const img = document.createElement('img')
                  img.src = item.image
                  img.alt = item.name || item.email || ''
                  img.className = 'h-full w-full object-cover'
                  avatar.appendChild(img)
                } else {
                  const initial = document.createElement('span')
                  initial.className = 'text-xs font-medium text-muted-foreground'
                  initial.textContent = (item.name || item.email || '?').charAt(0).toUpperCase()
                  avatar.appendChild(initial)
                }
                btn.appendChild(avatar)

                // Name text
                const nameSpan = document.createElement('span')
                nameSpan.textContent = item.name || item.email
                btn.appendChild(nameSpan)

                btn.onclick = () => {
                  if (currentCommand) {
                    currentCommand({ id: item.id, label: item.name || item.email })
                  }
                }
                wrap.appendChild(btn)
              })
              container.appendChild(wrap)
            }

            return {
              onStart: (props: any) => {
                mentionOpenRef.current = true
                selectedIndex = 0
                currentItems = props.items || []
                currentCommand = props.command
                container = document.createElement('div') as HTMLDivElement
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: container,
                  showOnCreate: true,
                  interactive: true,
                  placement: 'bottom-start',
                })
                renderList()
              },
              onUpdate: (props: any) => {
                selectedIndex = 0
                currentItems = props.items || []
                currentCommand = props.command
                renderList()
                popup[0].setProps({ getReferenceClientRect: props.clientRect })
              },
              onKeyDown: (props: any) => {
                const { event } = props
                if (currentItems.length === 0) return false
                if (event.key === 'ArrowUp') {
                  selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length
                  renderList()
                  return true
                }
                if (event.key === 'ArrowDown') {
                  selectedIndex = (selectedIndex + 1) % currentItems.length
                  renderList()
                  return true
                }
                if (event.key === 'Enter') {
                  const item = currentItems[selectedIndex]
                  if (item && currentCommand) {
                    currentCommand({ id: item.id, label: item.name || item.email })
                  }
                  return true
                }
                if (event.key === 'Escape') {
                  popup[0].hide()
                  return true
                }
                return false
              },
              onExit: () => {
                mentionOpenRef.current = false
                popup[0].destroy()
              },
            }
          },
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: `w-full rounded-md border bg-background mx-0 px-3 py-2 text-sm ${minHeightClass} ${maxHeightClass} focus:outline-none`,
      },
      handleKeyDown: (_view, event) => {
        // Let mention popup handle Enter/Escape when it's open
        if (mentionOpenRef.current) {
          return false
        }
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          const txt = editor?.getText() || ''
          const json = editor?.getJSON()
          const mentions = collectMentions(json, [])
          if (txt.trim()) {
            onSubmit?.({ text: txt.trim(), json, mentions })
            editor?.commands.clearContent(true)
          }
          return true
        }
        if (event.key === 'Escape') {
          onCancel?.()
          return true
        }
        return false
      },
    },
    autofocus: autoFocus ? 'end' : false,
    content: jsonValue || value || '',
    onUpdate: ({ editor }) => {
      const text = editor?.getText() || ''
      const json = editor?.getJSON()
      const mentions = collectMentions(json, [])

      onChangeContent?.(text, json, mentions)
    },
  })

  useEffect(() => {
    if (!editor) return
    const contentToSet = jsonValue || value
    if (!contentToSet) return
    // Try to set content when value or jsonValue changes
    try {
      editor.commands.setContent(contentToSet)
    } catch (e) {
      console.error('Error setting content', e)
    }
  }, [editor, value, jsonValue])

  // Clear content when value/jsonValue becomes an empty string or undefined (e.g. after submit)
  useEffect(() => {
    if (!editor) return
    // Only clear when both value and jsonValue are falsy
    if (!value && !jsonValue) {
      editor.commands.clearContent(true)
    }
  }, [editor, value, jsonValue])

  useEffect(() => {
    if (autoFocus) editor?.commands.focus('end')
  }, [editor, autoFocus])

  // Toolbar ability state
  const [_, setBump] = useState(0)
  useEffect(() => {
    if (!editor) return
    const rerender = () => setBump((n) => (n + 1) % 1000)
    editor.on('transaction', rerender)
    return () => {
      editor.off('transaction', rerender)
    }
  }, [editor])

  return (
    <div className='w-full space-y-2'>
      <div
        className={cn('w-full overflow-hidden rounded-lg', showBorder && 'border')}
      >
        <EditorContent
          editor={editor}
          className={cn(
            'w-full [&>div]:max-h-[288px] [&>div]:w-full [&>div]:overflow-y-auto [&>div]:border-none',
            editorClassName
          )}
        >
          <EmojiDropdownMenu />
        </EditorContent>
        <div className='flex items-center justify-between gap-2 px-2 py-1.5'>
          <div className='flex items-center gap-0'>
            <Button
              type='button'
              variant='ghost'
              size='xs'
              onClick={() => editor?.chain().focus().insertContent('@').run()}
              aria-label='Mention'
            >
              <AtSign className='text-muted-foreground h-4 w-4' />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='xs'
                  aria-label='Insert emoji'
                >
                  <Smile className='text-muted-foreground h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-[350px] border-none p-0'>
                {(() => {
                  const emojis: EmojiItem[] =
                    (editor as any)?.extensionStorage?.emoji?.emojis || []
                  return (
                    <EmojiMenu
                      emojis={emojis}
                      showSearch
                      onSelect={(emoji: EmojiItem) => {
                        editor?.chain().focus().setEmoji(emoji.name).run()
                      }}
                    />
                  )
                })()}
              </PopoverContent>
            </Popover>

            <Separator orientation='vertical' className='mx-1 !h-6' />

            <Button
              type='button'
              variant={editor?.isActive('bold') ? 'default' : 'ghost'}
              size='xs'
              onClick={() => editor?.chain().focus().toggleBold().run()}
              aria-label='Bold'
            >
              <Bold
                className={cn(
                  'text-muted-foreground h-4 w-4',
                  editor?.isActive('bold') && 'text-white'
                )}
              />
            </Button>
            <Button
              type='button'
              variant={editor?.isActive('italic') ? 'default' : 'ghost'}
              size='xs'
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              aria-label='Italic'
            >
              <Italic
                className={cn(
                  'text-muted-foreground h-4 w-4',
                  editor?.isActive('italic') && 'text-white'
                )}
              />
            </Button>
            <Button
              type='button'
              variant={editor?.isActive('strike') ? 'default' : 'ghost'}
              size='xs'
              onClick={() => editor?.chain().focus().toggleStrike().run()}
              aria-label='Strikethrough'
            >
              <Strikethrough
                className={cn(
                  'text-muted-foreground h-4 w-4',
                  editor?.isActive('strike') && 'text-white'
                )}
              />
            </Button>
            {/* 与UI沟通，先注释 */}
            {/* <Button
              type='button'
              variant='ghost'
              size='xs'
              onClick={() => {
                const url = window.prompt('Enter URL')
                if (!url) return
                editor
                  ?.chain()
                  .focus()
                  .extendMarkRange('link')
                  .setLink({ href: url })
                  .run()
              }}
              aria-label='Insert link'
            >
              <LinkIcon className='text-muted-foreground h-4 w-4' />
            </Button> */}
          </div>
          <div className='flex items-center gap-1.5'>
            {/* Emoji picker using TipTap Emoji extension data */}

            {onCancel && (
              <Button
                type='button'
                variant='ghost'
                size='xs'
                onClick={() => onCancel?.()}
                aria-label='Cancel'
              >
                <X className='h-4 w-4' />
              </Button>
            )}
            {showSubmitButton && (
              <Button
                type='button'
                size='xs'
                className='rounded-md'
                disabled={!(editor?.getText() || '').trim()}
                onClick={() => {
                  const txt = editor?.getText() || ''
                  const json = editor?.getJSON()
                  const mentions = collectMentions(json, [])
                  if (txt.trim()) {
                    onSubmit?.({ text: txt.trim(), json, mentions })
                    editor?.commands.clearContent(true)
                  }
                }}
                aria-label='Submit'
              >
                <ArrowUp className='h-4 w-4' />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
